/**
 * OverviewTab — Score breakdown and quality dimensions for the selected table.
 * Wired to real quality_dimensions data from the profiler backend.
 */

import { QualityDimensionBar } from "../data-display/QualityDimensionBar";
import type { TableDetail } from "./DetailPanel";
import { sampleCapTitle } from "../discovery/TableCard";

interface OverviewTabProps {
  table: TableDetail;
}

// The 4-axis DAMA-aligned quality radar. Backend `_table_quality_dimensions`
// emits these keys per table (same helper that feeds the Summary radar, so a
// table's deep-dive scores equal its contribution to the radar). DAMA's finer
// categories fold in: accuracy→validity, integrity→uniqueness,
// timeliness→consistency. The frontend iterates whatever keys the backend
// sent, so the labels/tooltips are keyed by both. (Accuracy is retained for
// the legacy fallback path below, which still emits a separate bar.)
const DIMENSION_LABELS: Record<string, string> = {
  completeness: "Completeness",
  validity: "Validity",
  uniqueness: "Uniqueness",
  consistency: "Consistency",
  accuracy: "Accuracy",
};

// Plain-English explanations of how each dimension is measured. Shown as the
// `title` on the bar label so users can hover to understand the percentage.
// These describe the CRITICALITY-WEIGHTED RECORD-LEVEL method: each axis is the
// worst of (a) structural conformance from column stats, (b) the % of the
// table's rows that pass the mapped DQ rules (record-level), and (c) a severity
// ceiling so a single critical finding still visibly dents the score.
const DIMENSION_TOOLTIPS: Record<string, string> = {
  Completeness:
    "Completeness = % of the table's rows that are populated — the non-null cell ratio across columns, combined with any completeness rules (report/demographic fields present). Taken as the worst of the two. Lower = significant nulls or missing required values.",
  Validity:
    "Validity = % of rows whose values are well-formed and correct — expected type/format/domain (dates parse, numerics in range, patterns match, no sentinels) plus accuracy rules (e.g. SSN format, plausible ages, no future dates). A critical/high finding caps the score even at low prevalence. Folds in the Accuracy dimension.",
  Uniqueness:
    "Uniqueness = % of rows conforming to uniqueness + integrity rules — no duplicate keys (e.g. duplicate SSNs) and no orphaned references (e.g. a placement pointing at a missing provider). A severity floor means one critical duplicate still dents the score even at low prevalence. Folds in the Integrity dimension.",
  Consistency:
    "Consistency = % of rows conforming to consistency + timeliness rules — cross-field agreement, valid date ordering, and no overdue/temporal violations (e.g. investigation before report date). A severity floor applies. Folds in the Timeliness dimension.",
  Accuracy:
    "Accuracy = how well the data reflects reality (orphan references, date-order violations). In the current radar this folds into Validity; shown separately only for legacy responses.",
};

export function OverviewTab({ table }: OverviewTabProps) {
  const dims = table.qualityDimensions;

  return (
    <div className="space-y-4">
      {/* Score summary */}
      <div className="flex items-center gap-3">
        <span className="text-heading-1 font-bold text-[var(--foreground)]">
          {table.score}
        </span>
        <div>
          <p className="text-body font-medium text-[var(--foreground)]">
            {table.severity}
          </p>
          <p className="text-caption text-[var(--muted-foreground)]">
            {table.columnCount} columns ·{" "}
            {table.sampleRowCount != null &&
            table.sampleRowCount > 0 &&
            table.sampleRowCount < table.rowCount
              ? `${table.sampleRowCount.toLocaleString()} rows profiled (${((table.sampleRowCount / table.rowCount) * 100).toFixed((table.sampleRowCount / table.rowCount) * 100 < 1 ? 2 : 1)}% sample of ${table.rowCount.toLocaleString()})`
              : `${table.rowCount.toLocaleString()} rows`}
          </p>
          {table.sampleCap && (
            <p
              data-testid="sample-capped"
              className="text-caption text-amber-400"
              title={sampleCapTitle(table.sampleCap)}
            >
              Size-capped sample: {table.sampleCap.rows_kept.toLocaleString()}{" "}
              of {table.sampleCap.rows_requested.toLocaleString()} requested
              rows were profiled (per-table memory limit)
            </p>
          )}
        </div>
      </div>

      {/* Quality dimensions — wired to backend data */}
      <div className="space-y-3 mt-6">
        <h3 className="text-body font-semibold text-[var(--foreground)]">
          Quality Dimensions
        </h3>
        {dims && Object.keys(dims).length > 0
          ? Object.entries(dims).map(([key, value]) => {
              const label =
                DIMENSION_LABELS[key] ||
                key.charAt(0).toUpperCase() + key.slice(1);
              return (
                <div key={key} title={DIMENSION_TOOLTIPS[label]}>
                  <QualityDimensionBar
                    label={label}
                    value={typeof value === "number" ? value : 0}
                  />
                </div>
              );
            })
          : /* Fallback: compute rough dimensions from column stats */
            [
              "Completeness",
              "Validity",
              "Uniqueness",
              "Consistency",
              "Accuracy",
            ].map((dim) => {
              let value = 0;
              const cols = table.columns || [];
              if (cols.length > 0) {
                switch (dim) {
                  case "Completeness":
                    value = Math.round(
                      cols.reduce((s, c) => s + (100 - (c.nullRate || 0)), 0) /
                        cols.length,
                    );
                    break;
                  case "Uniqueness":
                    value = Math.round(
                      cols.reduce(
                        (s, c) => s + (c.nullRate !== undefined ? 70 : 50),
                        0,
                      ) / cols.length,
                    );
                    break;
                  default:
                    value = Math.round(table.score * 0.9);
                }
              }
              return (
                <div key={dim} title={DIMENSION_TOOLTIPS[dim]}>
                  <QualityDimensionBar label={dim} value={value} />
                </div>
              );
            })}
      </div>

      {/* Column stats summary */}
      {table.columns && table.columns.length > 0 && (
        <div className="space-y-2 mt-6">
          <h3 className="text-body font-semibold text-[var(--foreground)]">
            Column Highlights
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <StatChip
              label="PII Columns"
              value={table.columns.filter((c) => c.isPII).length}
              tip="Columns the PII detector flagged (names, emails, SSNs, phones, addresses, DOB, etc.). Count zero means nothing matched the detector's regex + column-name heuristics — NOT a guarantee the table is PII-free."
            />
            <StatChip
              label="Primary Keys"
              value={table.columns.filter((c) => c.isPK).length}
              tip="Columns in the table's PK — either declared by the database (constraint) or inferred by the profiler (uniqueness ≥ 99% + non-null). Composite PKs count every participating column."
            />
            <StatChip
              label="Foreign Keys"
              value={table.columns.filter((c) => c.isFK).length}
              tip="Columns identified as FK references — declared by the database, or inferred by name-match + type-match + value-overlap against PKs in other tables."
            />
            <StatChip
              label="Nullable"
              value={table.columns.filter((c) => (c.nullRate || 0) > 0).length}
              tip="Columns with at least one NULL value in the sampled data. Not the same as the schema nullability constraint — this reflects observed nulls."
            />
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({
  label,
  value,
  tip,
}: {
  label: string;
  value: number;
  tip?: string;
}) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 cursor-help"
      title={tip}
      aria-label={tip ? `${label}: ${value}. ${tip}` : `${label}: ${value}`}
    >
      <span className="text-caption text-[var(--muted-foreground)]">
        {label}
      </span>
      <span className="text-body font-medium text-[var(--foreground)]">
        {value}
      </span>
    </div>
  );
}
